import { ApolloClient, ApolloQueryResult } from 'apollo-client';
import { ApolloLink, FetchResult, Observable } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import { RetryLink } from 'apollo-link-retry';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { DocumentNode } from 'graphql';
import QueueLink from 'apollo-link-queue';
import { ENDPOINTS } from '@/settings/endpoints';
import { SubscriptionClient } from 'subscriptions-transport-ws';

export enum MICROSERVICES_ENUM {
  CROW = 'crow',
  CROW_WS = 'crow_ws',
  REACTIONS = 'reaction',
  PROFILE = 'profile',
  MICROCERTIFICATES = 'microcertificates',
  ENROLLMENTS = 'enrollments',
  BOARDSGROUPS = 'boardsgroups',
  STUDYPLANNING = 'studyplanning',
  ASSESSMENTS = 'assessments',
  STUDENTNOTES = 'studentnotes',
  KINGSLANDING = 'kingslanding',
}

export enum MICROSERVICES_AUTHORIZATION_ENUM {
  PROFILE = 'profileToken',
  DEX = 'token',
}

const MICROSERVICES = {
  crow: { url: ENDPOINTS.westerosGraphql.CROW_ENDPOINT, webSocket: false },
  crow_ws: { url: ENDPOINTS.westerosGraphql.CROW_WS_ENDPOINT, webSocket: true },
  reaction: { url: ENDPOINTS.westerosGraphql.REACTION_ENDPOINT, webSocket: false },
  profile: { url: ENDPOINTS.westerosGraphql.PROFILE_ENDPOINT, webSocket: false },
  microcertificates: {
    url: ENDPOINTS.westerosGraphql.MICROCERTIFICATES_ENDPOINT,
    webSocket: false,
  },
  enrollments: { url: ENDPOINTS.westerosGraphql.ENROLLMENTS_ENDPOINT, webSocket: false },
  boardsgroups: { url: ENDPOINTS.boardsgroups, webSocket: false },
  studyplanning: { url: ENDPOINTS.postgradStudyPlanning, webSocket: false },
  studentnotes: { url: ENDPOINTS.westerosGraphql.STUDENTNOTES_ENDPOINT, webSocket: false },
  assessments: { url: ENDPOINTS.postgradAssessments, webSocket: false },
  kingslanding: { url: ENDPOINTS.westerosGraphql.KINGSLANDING_ENDPOINT, webSocket: false },
};

const offlineLink = new QueueLink();

window.addEventListener('offline', () => offlineLink.close());
window.addEventListener('online', () => offlineLink.open());

export const apolloClient: any = {};

const getApolloClient = (
  microservice: MICROSERVICES_ENUM,
  authorizationType: MICROSERVICES_AUTHORIZATION_ENUM,
  customHeaders: { [key: string]: any } | undefined,
): ApolloClient<NormalizedCacheObject> => {
  if (!apolloClient[`${microservice}${authorizationType}`]) {
    const { url, webSocket } = MICROSERVICES[microservice];
    const token = localStorage.getItem(authorizationType);
    const authorization = token ? `Bearer ${token.replace(/"/g, '')}` : '';
    const links = [];

    if (url && webSocket) {
      const subClient = new SubscriptionClient(url, {
        reconnect: true,
        connectionParams: {
          Authorization: authorization,
        },
      });
      subClient.onConnected(() => {});
      subClient.onReconnected(() => {});
      subClient.onDisconnected(() => {});
      subClient.onError(() => {});

      const wsLink = new WebSocketLink(subClient);

      links.push(wsLink);
    } else {
      const httpLink = createHttpLink({
        uri: url,
        headers: {
          authorization,
          ...customHeaders,
        },
        fetch,
      });

      links.push(new RetryLink());
      links.push(offlineLink);
      links.push(httpLink);
    }

    const link = ApolloLink.from(links);

    apolloClient[`${microservice}${authorizationType}`] = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      connectToDevTools: true,
      defaultOptions: {
        query: {
          fetchPolicy: 'no-cache',
        },
      },
    });
  }

  return apolloClient[`${microservice}${authorizationType}`];
};

export const graphqlQuery = async <T = any, R = Record<string, any>>(
  microservice: MICROSERVICES_ENUM,
  query: DocumentNode,
  variables?: R,
  authorization?: MICROSERVICES_AUTHORIZATION_ENUM,
  customHeaders?: { [key: string]: any },
): Promise<ApolloQueryResult<T>> => {
  const client = getApolloClient(
    microservice,
    authorization || MICROSERVICES_AUTHORIZATION_ENUM.DEX,
    customHeaders,
  );

  return client.query<T, R>({
    query,
    variables,
  });
};

export const graphqlMutation = async <T = any, R = Record<string, any>>(
  microservice: MICROSERVICES_ENUM,
  mutation: DocumentNode,
  variables?: R,
  authorization?: MICROSERVICES_AUTHORIZATION_ENUM,
  customHeaders?: { [key: string]: any },
): Promise<FetchResult<T>> => {
  const client = getApolloClient(
    microservice,
    authorization || MICROSERVICES_AUTHORIZATION_ENUM.DEX,
    customHeaders,
  );

  return client.mutate<T, R>({
    mutation,
    variables,
  });
};

export const graphqlSubscription = (
  microservice: MICROSERVICES_ENUM,
  query: DocumentNode,
  variables?: { [key: string]: any },
  authorization?: MICROSERVICES_AUTHORIZATION_ENUM,
  customHeaders?: { [key: string]: any },
): Observable<any> => {
  const client = getApolloClient(
    microservice,
    authorization || MICROSERVICES_AUTHORIZATION_ENUM.DEX,
    customHeaders,
  );

  return client.subscribe<any>({ query, variables });
};
